import { createRoute, Outlet, redirect, useLocation } from "@tanstack/solid-router";
import { rootRoute } from "../__root";
import { api } from "@/lib/api";
import { sessionActor } from "@/lib/session-machine";
import { queryClient } from "@/lib/query-client";
import { appState, PageTransition } from "@fleet/ui";
import { cloneSession, isValidSession, queryKeys } from "@stockpilot/shared";
import { SESSION_CHECK_TIMEOUT_MS } from "@/lib/constants";

/**
 * Stockpilot auth wrapper.
 *
 * NOTE (#13 — de-clone): stockpilot deliberately does NOT use the shared
 * `AuthLayout` (the split brand-panel + centered glass `AuthCard` shell every
 * clone inherited). Instead each screen owns its own `AuthConsole` — a warehouse
 * operations-console shell (utility rail + dock-tag header + flat field stack on
 * a left accent rail, no glass card). So this route is a thin wrapper: it only
 * owns the "already authenticated → redirect" gate and the page transition. The
 * background, status rail and scroll container live in `AuthConsole`, rendered
 * per-screen.
 */
function AuthLayoutRoute() {
  const location = useLocation();
  appState.markAsReady();

  return (
    <PageTransition routeKey={location().pathname}>
      <Outlet />
    </PageTransition>
  );
}

export const authRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "_auth",
  // If already authenticated, redirect to the app root.
  beforeLoad: async ({ search }) => {
    // Multi-account: `?mode=add` opens /login to ADD another account while the
    // current one stays signed in (Gmail style). The "already authenticated →
    // go home" gate must be skipped here; otherwise the switcher's "Add account"
    // would bounce straight back to the app and a 2nd login could never be added.
    const isAddingAccount = (search as { mode?: string }).mode === "add";
    if (isAddingAccount) {
      return;
    }

    // Fast path: check machine state before hitting the network.
    const snapshot = sessionActor.getSnapshot();
    if (!snapshot.matches("priming") && (snapshot.context.session || snapshot.context.isVerified)) {
      if (snapshot.context.session) {
        // oxlint-disable-next-line @typescript-eslint/only-throw-error
        throw redirect({ to: (search as { redirect?: string }).redirect ?? "/" });
      }
      return;
    }

    // Network check — only if machine has no session yet.
    // Use api.GET (not fetchApi) so we can inspect the status without throwing.
    //
    // P23/CC-60 · I1-bis: this `await` must NOT be unbounded. A STALLED
    // network request (held, neither 401 nor 5xx: both resolve quickly) left this
    // `beforeLoad` unresolved, router without mounting the route and entire app IN
    // BLANK — measured here: empty `innerText` at 3/6/10/15/20 s on `/login`,
    // on dev server and production bundle, while `/` rendered at
    // 3 s because sister guard in `_app` DID run with a ceiling. That ceiling was
    // a local literal there, so never reached here: now both read
    // `SESSION_CHECK_TIMEOUT_MS` from `@/lib/constants`.
    //
    // The request is NOT canceled: it stays alive and synchronizes machine + cache when
    // it arrives. If a session turns out to exist, `BootManager` takes the user out of the
    // login screen; at most there is a flicker, never a blank screen.
    const fetchSession = async () =>
      api.GET("/api/auth/get-session", { params: { query: { jwt: true } } });
    type FetchResult = Awaited<ReturnType<typeof fetchSession>>;

    const syncReply = (reply: FetchResult) => {
      const { data: replyData, error: replyError } = reply;
      // Synchronize the machine and the cache immediately.
      // This prevents the BootManager from seeing a "pending" or stale session
      // during the route transition, which causes redirection loops.
      sessionActor.send({
        type: "SERVER_REPLY",
        session: isValidSession(replyData) ? cloneSession(replyData) : null,
        error: replyError ?? null,
      });
      queryClient.setQueryData(queryKeys.auth.session(), replyData ?? null);
      return reply;
    };

    const inFlight = fetchSession()
      .then(syncReply)
      // Network error — let user try to log in anyway.
      .catch(() => null);

    const timedOut = new Promise<null>((resolve) => {
      setTimeout(() => {
        resolve(null);
      }, SESSION_CHECK_TIMEOUT_MS);
    });

    const result = await Promise.race([inFlight, timedOut]);
    if (!result) {
      // Deadline expired or network error: the login is RENDERED. The late response
      // stays hooked above (synchronizes machine + cache) and `BootManager`
      // redirects later if a session turned out to exist.
      return;
    }

    // Redirect is thrown OUTSIDE the try/catch so it propagates to TanStack Router.
    // Decide from the session the machine accepted, not from the raw reply: a stale
    // get-session that LOGOUT revoked must not bounce the guest off /login.
    if (sessionActor.getSnapshot().context.session) {
      // oxlint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: (search as { redirect?: string }).redirect ?? "/" });
    }
    // 401/403, network error, or null session — not authenticated, show login page.
  },
  component: AuthLayoutRoute,
});
